import { Fragment } from 'react';
import { useReduxState } from 'hooks';
import { EditorBlockTranslationReference } from 'components/editor';
import { Button, RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { FormGroup } from 'components/common/form';
import { TextEditor } from 'components/editor/inputs';
import { TaskPointWeightSelector } from 'components/common/tasks';
import {
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo
} from 'components/editor/blocks';
import {
  EditorBlockChoiceTasksMoreOptionsSettings,
  EditorBlockChoiceTasksOption,
  EditorBlockChoiceTasksResponseRates,
  EditorBlockChoiceTasksScoringSettings,
  EditorBlockChoiceTasksSelectionSettings
} from '.';

import type { BlockMultipleChoice } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

export type BlockChoiceTasksStateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

type Translatable = BlockMultipleChoice['locales'][string]['translatable'];

type Props = BlockChoiceTasksStateProps &
  Pick<
    BlockProps<BlockMultipleChoice>,
    | 'blockId'
    | 'data'
    | 'isAIPreview'
    | 'isPreview'
    | 'languageVersion'
    | 'updateData'
  > & {
    blockName: string;
    defaultPointsWeight: number;
    partialFeedbackForm?: React.ReactNode;
    taskQuestionPlaceHolder: string;
  };

// this component is shared by single choice and multiple choice tasks
const EditorBlockChoiceTasks = ({
  blockId,
  blockName,
  data,
  defaultPointsWeight,
  isAIPreview,
  isPreview,
  languageVersion,
  partialFeedbackForm,
  taskQuestionPlaceHolder,
  updateData,
  ...stateProps
}: Props) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, serverConfig, theme } = useReduxState();

  const handleFeedbackChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedback: html
          }
        }
      }
    });
  };

  const handleFeedbackCorrectChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedbackCorrect: html
          }
        }
      }
    });
  };

  const handleFeedbackIncorrectChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedbackIncorrect: html
          }
        }
      }
    });
  };

  const handleFeedbackTypeChange = (
    feedbackType: BlockMultipleChoice['feedbackType']
  ) => {
    data.feedbackType = feedbackType;
    if (
      data.locales[languageVersion].translatable.feedback &&
      feedbackType === 'answer-specific' &&
      data.options.every(
        (_, index) =>
          !data.locales[languageVersion].translatable[
            `option_${index + 1}_feedback`
          ] ||
          data.locales[languageVersion].translatable[
            `option_${index + 1}_feedback`
          ] === '<p></p>'
      )
    ) {
      // add common feedback to options
      data.options.forEach((option, index) => {
        const translatableKey = `option_${index + 1}_feedback` as const;
        data.locales[languageVersion].translatable[translatableKey] =
          data.locales[languageVersion].translatable.feedback;

        option.feedback = `{{ locales.translatable.${translatableKey} }}`;
      });
    }
    updateData(data);
  };

  const handleOptionAdd = () => {
    const previousOptionCount = data.options.length;
    const newOptionNumber = data.options.length + 1;
    // create a new option with locale string placeholders
    data.options.push({
      text: `{{ locales.translatable.option_${newOptionNumber}_text }}`,
      correct: false,
      feedback: `{{ locales.translatable.option_${newOptionNumber}_feedback }}`
    });
    if (!data.locales[languageVersion].translatable) {
      data.locales[languageVersion].translatable = {} as Translatable;
    }
    if (
      !data.locales[languageVersion].translatable[
        `option_${newOptionNumber}_text`
      ]
    ) {
      data.locales[languageVersion].translatable[
        `option_${newOptionNumber}_text`
      ] = '';
    }
    if (
      !data.locales[languageVersion].translatable[
        `option_${newOptionNumber}_feedback`
      ]
    ) {
      data.locales[languageVersion].translatable[
        `option_${newOptionNumber}_feedback`
      ] = '';
    }

    if (data.numberOfAllowedSelectionsMax === previousOptionCount) {
      data.numberOfAllowedSelectionsMax = data.options.length;
    }

    updateData(data);
    setTimeout(() => {
      const optionContentEditable = document
        .getElementById(
          `multiple-choice-option-${blockId}-${data.options.length - 1}`
        )
        ?.querySelector('div[contenteditable="true"]');
      if (optionContentEditable) (optionContentEditable as HTMLElement).focus();
    });
  };

  const handleOptionCorrectChange = (index: number, value: boolean) => {
    const previousCorrectOptionsCount = data.options.filter(
      (option) => option.correct
    ).length;

    if (data.isSingleChoice && value) {
      // if single-choice and selecting this option as correct, unselect all others
      data.options = data.options.map((option, i) => ({
        ...option,
        correct: i === index
      }));
    } else {
      data.options[index].correct = !!value;
    }

    if (!data.options.some((option) => option.correct)) {
      if (data.feedbackType === 'result-based') data.feedbackType = 'default';
    }

    data.requiredNumberOfCorrectAnswers = updateNumberOfCorrectAnswers(
      data,
      previousCorrectOptionsCount
    );
    updateData(data);
  };

  // deleting an option is a bit complex, because we need to shift the indexes from the
  // translatable keys and their matching locales placeholders of the other options.
  // if we have option_1_text, option_2_text and option_3_text and we delete option 2,
  // then the old option_3_text becomes option_2_text etc.
  const handleOptionDelete = (index: number) => {
    // save the original count before deleting option
    const oldOptionCount = data.options.length;

    // remove the option from the options array
    data.options.splice(index, 1);

    const translatable = data.locales[languageVersion].translatable;

    // delete the translatable keys for the option being removed
    delete translatable[`option_${index + 1}_text`];
    delete translatable[`option_${index + 1}_feedback`];

    // shift keys for options that follow
    for (let j = index + 1; j < oldOptionCount; j++) {
      translatable[`option_${j}_text`] = translatable[`option_${j + 1}_text`];
      translatable[`option_${j}_feedback`] =
        translatable[`option_${j + 1}_feedback`];
    }

    // clean up the unused keys
    delete translatable[`option_${oldOptionCount}_text`];
    delete translatable[`option_${oldOptionCount}_feedback`];

    // update the placeholders in the options array
    data.options.forEach((option, i) => {
      option.text = `{{ locales.translatable.option_${i + 1}_text }}`;
      if (option.feedback) {
        option.feedback = `{{ locales.translatable.option_${i + 1}_feedback }}`;
      }
    });

    if (data.numberOfAllowedSelectionsMax > data.options.length) {
      data.numberOfAllowedSelectionsMax = data.options.length;
    }

    if (data.numberOfAllowedSelectionsMin > data.numberOfAllowedSelectionsMax) {
      data.numberOfAllowedSelectionsMin = data.numberOfAllowedSelectionsMax;
    }

    data.requiredNumberOfCorrectAnswers = updateNumberOfCorrectAnswers(data);

    updateData(data);
  };

  const handleOptionFeedbackChange = (index: number, feedback: string) => {
    const translatableKey = `option_${index + 1}_feedback` as const;
    if (!data.locales[languageVersion].translatable) {
      data.locales[languageVersion].translatable = {} as Translatable;
    }

    const setFocus =
      !data.locales[languageVersion].translatable[translatableKey];

    data.locales[languageVersion].translatable[translatableKey] = feedback;
    data.options[
      index
    ].feedback = `{{ locales.translatable.${translatableKey} }}`;

    updateData(data);

    if (setFocus) {
      setTimeout(() => {
        const feedbackContentEditable = document
          .getElementById(`multiple-choice-option-${blockId}-${index}`)
          ?.querySelector('.feedback div[contenteditable="true"]');
        if (feedbackContentEditable) {
          (feedbackContentEditable as HTMLElement).focus();
        }
      });
    }
  };

  const handleOptionTextChange = (index: number, text: string) => {
    if (!data.locales[languageVersion].translatable) {
      data.locales[languageVersion].translatable = {} as Translatable;
    }

    const translatableKey = `option_${index + 1}_text`;
    data.locales[languageVersion].translatable[translatableKey] = text;
    updateData(data);
  };

  const handlePointsWeightChange = (value: number) => {
    updateData({ ...data, pointsWeight: value });
  };

  const handleQuestionChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            question: html
          }
        }
      }
    });
  };

  const handleRandomizeOptions = (value: boolean) => {
    updateData({ ...data, randomizeOptions: value });
  };

  const handleRequiredNumberOfCorrectAnswers = (value: number) => {
    updateData({ ...data, requiredNumberOfCorrectAnswers: value });
  };

  const handleNumberOfAllowedSelectionsMin = (value?: number) => {
    const valueToSet = typeof value === 'number' ? value : 0;
    const currentMinimumValue =
      typeof data.numberOfAllowedSelectionsMin === 'number'
        ? data.numberOfAllowedSelectionsMin
        : 0;

    if (valueToSet === currentMinimumValue) {
      return;
    }

    updateData({
      ...data,
      numberOfAllowedSelectionsMin: valueToSet
    });
  };

  const handleNumberOfAllowedSelectionsMax = (value?: number) => {
    const valueToSet = typeof value === 'number' ? value : data.options.length;
    const currentMaximumValue =
      typeof data.numberOfAllowedSelectionsMax === 'number'
        ? data.numberOfAllowedSelectionsMax
        : data.options.length;

    if (valueToSet === currentMaximumValue) {
      return;
    }

    updateData({
      ...data,
      numberOfAllowedSelectionsMax: valueToSet
    });
  };

  const handleNoPointReductionForWrongSelections = (value: boolean) => {
    updateData({ ...data, noPointReductionForWrongSelections: value });
  };

  const handleShowResponseRatesAfterSubmitChange = (value: boolean) => {
    data.showResponseRatesAfterSubmit = value;
    updateData(data);
  };

  const updateNumberOfCorrectAnswers = (
    updatedData: typeof data,
    previousCorrectOptionsCount?: number
  ) => {
    const numberOfCorrectOptions = updatedData.options.filter(
      (o) => o.correct
    ).length;
    const currentRequiredNumberOfCorrectAnswers =
      updatedData.requiredNumberOfCorrectAnswers || 0;

    const shouldKeepAllCorrectAnswersRequired =
      typeof previousCorrectOptionsCount === 'number' &&
      previousCorrectOptionsCount > 0 &&
      currentRequiredNumberOfCorrectAnswers === previousCorrectOptionsCount &&
      numberOfCorrectOptions > previousCorrectOptionsCount;

    if (shouldKeepAllCorrectAnswersRequired) {
      return numberOfCorrectOptions;
    }

    if (
      !currentRequiredNumberOfCorrectAnswers ||
      numberOfCorrectOptions < currentRequiredNumberOfCorrectAnswers
    ) {
      return numberOfCorrectOptions;
    } else {
      return currentRequiredNumberOfCorrectAnswers;
    }
  };

  const pointsWeight = data.hasOwnProperty('pointsWeight')
    ? data.pointsWeight
    : defaultPointsWeight;

  const correctAnswerSet = data.options.filter((o) => o.correct).length !== 0;

  const previewFeedbackStyle = {
    backgroundColor: 'white',
    border: `1px solid ${theme.lightGreyDark}`,
    borderRadius: theme.inputBorderRadius,
    color: theme.textColorLight,
    padding: 10
  };

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="multiplechoice">
      <FormGroup maxWidth="100%">
        {!isAIPreview ? (
          <TaskPointWeightSelector
            blockId={blockId}
            blockName={blockName}
            disabled={isPreview}
            isMandatory={data.isMandatory}
            pointsWeight={pointsWeight}
            onSelection={handlePointsWeightChange}
          />
        ) : null}

        {isAIPreview || isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.question}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.question ||
                ''
              }
              translationHtml={
                data.locales[languageVersion].translatable.question
              }
            >
              <TextEditor
                ariaLabel={messages.commonQuestion}
                backgroundColor="inherit"
                content={data.locales[languageVersion].translatable.question}
                onChange={handleQuestionChange}
                padding="0"
                placeholder={taskQuestionPlaceHolder}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      <FormGroup
        label={messages.blocksMultipleChoiceOptions}
        maxWidth="100%"
        style={{ marginTop: 25 }}
      >
        {!isAIPreview && !correctAnswerSet ? (
          <div className="options-info">
            {messages.editorBlockMultipleChoiceOptionsInfo}
          </div>
        ) : null}

        <div className="options">
          {data.options &&
            data.options.map((option, i) => {
              const optionTextLocaleKey = `option_${i + 1}_text`;
              const optionFeedbackLocaleKey = `option_${i + 1}_feedback`;
              return (
                <Fragment key={i}>
                  <EditorBlockTranslationReference
                    backgroundColor={theme.lightGrey}
                    key={editorLanguageReference + i}
                    isInnerElement={true}
                    referenceHtml={
                      data.locales[editorLanguageReference]?.translatable?.[
                        optionTextLocaleKey
                      ] || ''
                    }
                    style={{ marginTop: 10 }}
                    translationHtml={
                      data.locales[languageVersion].translatable?.[
                        optionTextLocaleKey
                      ]
                    }
                  >
                    <EditorBlockChoiceTasksOption
                      disableCorrectChange={isEditingLanguageVersion}
                      correct={option.correct}
                      deleteIsDisabled={
                        data.options.length === 1 || isEditingLanguageVersion
                      }
                      feedback={
                        data.locales[languageVersion].translatable?.[
                          optionFeedbackLocaleKey
                        ] || ''
                      }
                      feedbackTranslationReference={
                        isEditingLanguageVersion
                          ? data.locales[editorLanguageReference]
                              ?.translatable?.[optionFeedbackLocaleKey]
                          : null
                      }
                      id={`multiple-choice-option-${blockId}-${i}`}
                      index={i}
                      isAIPreview={isAIPreview}
                      isPreview={isPreview}
                      isSingleChoice={data.isSingleChoice}
                      key={i}
                      onCorrectChange={handleOptionCorrectChange}
                      onDelete={handleOptionDelete}
                      onFeedbackChange={handleOptionFeedbackChange}
                      onTextChange={handleOptionTextChange}
                      showFeedbackEditor={
                        data.feedbackType === 'answer-specific'
                      }
                      text={
                        data.locales[languageVersion].translatable?.[
                          optionTextLocaleKey
                        ] || ''
                      }
                    />
                  </EditorBlockTranslationReference>
                </Fragment>
              );
            })}
        </div>
      </FormGroup>

      {isAIPreview || isPreview || isEditingLanguageVersion ? null : (
        <div className="add-option">
          <Button
            category="light"
            icon="plus"
            onClick={handleOptionAdd}
            size="medium"
            text={messages.commonAddOption}
          />
        </div>
      )}

      {!isAIPreview &&
      !isPreview &&
      !isEditingLanguageVersion &&
      !data.isSingleChoice ? (
        <>
          <EditorBlockChoiceTasksSelectionSettings
            blockId={blockId}
            disabled={isPreview}
            maxNumberOfOptions={data.options.length}
            numberOfAllowedSelectionsMax={data.numberOfAllowedSelectionsMax}
            numberOfAllowedSelectionsMin={data.numberOfAllowedSelectionsMin}
            onMaximumSelectionsChange={handleNumberOfAllowedSelectionsMax}
            onMinimumSelectionsChange={handleNumberOfAllowedSelectionsMin}
          />

          <EditorBlockChoiceTasksScoringSettings
            blockId={blockId}
            correctOptionsCount={
              data.options.filter((option) => option.correct).length
            }
            noPointReductionForWrongSelections={
              data.noPointReductionForWrongSelections
            }
            onNoPointReductionForWrongSelectionsChange={
              handleNoPointReductionForWrongSelections
            }
            onRequiredNumberOfCorrectAnswersChange={
              handleRequiredNumberOfCorrectAnswers
            }
            requiredNumberOfCorrectAnswers={data.requiredNumberOfCorrectAnswers}
          />
        </>
      ) : null}

      {correctAnswerSet && data.feedbackType === 'result-based' ? (
        <>
          <FormGroup
            label={
              <div className="result-label">
                <div className="result-label-icon">
                  <Icon
                    icon="check-circle"
                    style={{ color: theme.successStandaloneColor }}
                  />
                </div>
                {messages.editorBlockQuestionFeedbackCorrect}
              </div>
            }
            maxWidth="100%"
          >
            {isAIPreview || isPreview ? (
              <RichTextValue
                html={
                  data.locales[languageVersion].translatable.feedbackCorrect ||
                  '-'
                }
                style={previewFeedbackStyle}
              />
            ) : (
              <>
                <EditorBlockTranslationReference
                  backgroundColor={theme.lightGrey}
                  isInnerElement={true}
                  referenceHtml={
                    data.locales[editorLanguageReference]?.translatable
                      ?.feedbackCorrect || ''
                  }
                  style={{ marginTop: 5 }}
                  translationHtml={
                    data.locales[languageVersion].translatable.feedbackCorrect
                  }
                >
                  <TextEditor
                    colorPickerEnabled={
                      serverConfig.multipleChoiceUseAltFeedbackStyle
                    }
                    content={
                      data.locales[languageVersion].translatable
                        .feedbackCorrect || ''
                    }
                    onChange={handleFeedbackCorrectChange}
                    placeholder={messages.commonFeedbackPlaceholder}
                    showBorder={true}
                  />
                </EditorBlockTranslationReference>
              </>
            )}
          </FormGroup>

          {partialFeedbackForm || null}

          <FormGroup
            label={
              <div className="result-label">
                <div className="result-label-icon">
                  <Icon
                    icon="times-circle"
                    style={{ color: theme.dangerColor }}
                  />
                </div>
                {messages.editorBlockQuestionFeedbackIncorrect}
              </div>
            }
            maxWidth="100%"
          >
            {isAIPreview || isPreview ? (
              <RichTextValue
                html={
                  data.locales[languageVersion]?.translatable
                    ?.feedbackIncorrect || '-'
                }
                style={previewFeedbackStyle}
              />
            ) : (
              <>
                <EditorBlockTranslationReference
                  backgroundColor={theme.lightGrey}
                  isInnerElement={true}
                  referenceHtml={
                    data.locales[editorLanguageReference]?.translatable
                      ?.feedbackIncorrect || ''
                  }
                  style={{ marginTop: 5 }}
                  translationHtml={
                    data.locales[languageVersion].translatable.feedbackIncorrect
                  }
                >
                  <TextEditor
                    colorPickerEnabled={
                      serverConfig.multipleChoiceUseAltFeedbackStyle
                    }
                    content={
                      data.locales[languageVersion].translatable
                        .feedbackIncorrect || ''
                    }
                    onChange={handleFeedbackIncorrectChange}
                    placeholder={messages.commonFeedbackPlaceholder}
                    showBorder={true}
                  />
                </EditorBlockTranslationReference>
              </>
            )}
          </FormGroup>
        </>
      ) : !data.feedbackType || data.feedbackType === 'default' ? (
        <FormGroup
          label={messages.editorBlockQuestionFeedbackText}
          maxWidth="100%"
        >
          {isAIPreview || isPreview ? (
            <RichTextValue
              html={data.locales[languageVersion].translatable.feedback || '-'}
              style={previewFeedbackStyle}
            />
          ) : (
            <>
              <EditorBlockTranslationReference
                backgroundColor={theme.lightGrey}
                key={editorLanguageReference + 'feedback'}
                isInnerElement={true}
                referenceHtml={
                  data.locales[editorLanguageReference]?.translatable
                    ?.feedback || ''
                }
                style={{ marginTop: 5 }}
                translationHtml={
                  data.locales[languageVersion].translatable.feedback
                }
              >
                <TextEditor
                  colorPickerEnabled={
                    serverConfig.multipleChoiceUseAltFeedbackStyle
                  }
                  content={data.locales[languageVersion].translatable.feedback}
                  onChange={handleFeedbackChange}
                  placeholder={messages.commonFeedbackPlaceholder}
                  showBorder={true}
                />
              </EditorBlockTranslationReference>
            </>
          )}
        </FormGroup>
      ) : null}

      {!isAIPreview && !isEditingLanguageVersion ? (
        <EditorBlockMoreSettings
          settingsHaveBeenEdited={
            !isPreview &&
            Boolean(
              data?.feedbackType === 'answer-specific' ||
                data?.feedbackType === 'result-based' ||
                data?.isMandatory ||
                (serverConfig.multipleChoiceEnableRandomizeOptions &&
                  data?.randomizeOptions) ||
                (serverConfig.multipleChoiceEnableResponseRates &&
                  data?.showResponseRatesAfterSubmit)
            )
          }
        >
          <EditorBlockIsMandatorySettings
            blockId={blockId}
            data={data}
            disabled={isPreview}
            updateData={updateData}
          />

          <EditorBlockChoiceTasksMoreOptionsSettings
            blockId={blockId}
            correctAnswerSet={correctAnswerSet}
            data={data}
            disabled={isPreview}
            enableRandomizer={serverConfig.multipleChoiceEnableRandomizeOptions}
            onFeedbackTypeChange={handleFeedbackTypeChange}
            onRandomizeOptionsChange={handleRandomizeOptions}
          />

          {serverConfig.multipleChoiceEnableResponseRates && (
            <EditorBlockChoiceTasksResponseRates
              blockId={blockId}
              data={data}
              disabled={isPreview}
              onShowResponseRatesAfterSubmitChange={
                handleShowResponseRatesAfterSubmitChange
              }
            />
          )}
        </EditorBlockMoreSettings>
      ) : null}

      {!isAIPreview ? (
        <EditorBlockRequireAnswerToProceedInfo data={data} />
      ) : null}

      <style jsx>{`
        .multiplechoice {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          counter-reset: multiple-choice-options;
          padding: 15px;
        }
        .options-info {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: normal;
          margin: -3px 0 10px;
        }
        .add-option {
          margin-top: 10px;
        }
        .result-label {
          align-items: center;
          display: flex;
        }
        .result-label-icon {
          font-size: 1.1rem;
          margin-right: 5px;
          position: relative;
          top: 1px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockChoiceTasks;
